import React, { useState } from "react";
import styled from "styled-components";
import { CourseMenu, DeleteCourseModal, HideCourseModal } from "./CourseMenu";
import { BooleanLiteral, ICourse } from "utils/types";
import { useModalStore } from "store";

const CardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 160px;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const CardBadge = styled.div<{ isPremium: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  color: white;
  background-color: ${(props) => (props.isPremium ? "#FFB800" : "#5575F7")};
`;

const MoreOptionsButton = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background-color: rgba(255, 255, 255, 1);
  }
`;

const CardContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CardTitle = styled.h3`
  font-family: Manrope-Medium;
  font-size: 16px;
  line-height: 22px;
  color: #333;
  margin: 0 0 8px 0;
`;

const QuestionsAvailableText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 4px 0;
`;

const YearsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const YearBadge = styled.div`
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 200px;
  background-color: #F6F8FF;
  color: #5575F7;
  `;
  const HiddenBadge = styled(YearBadge)`
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 200px;
  background-color: #f6f8ffe8;
  color: #d89e0c;
  width: fit-content;
  position: absolute;
  top: 12px;
  right: calc(50% - 30px);
  padding: 4px 8px;
`;

const MoreYearsBadge = styled(YearBadge)`
  color: #5575f7;
`;

export interface CourseCardProps {
  course: ICourse;
  onSelect?: (course: ICourse) => void;
  onDeleteCourse: (courseId: string) => Promise<boolean>;
  onHideCourse: (courseId: string, shouldHide: BooleanLiteral) => Promise<boolean>;
  onDeleteQuestions: (courseId: string) => Promise<boolean>;
  onEditDetails: (course: ICourse) => void;
  onMoveToAddQuestions: (course: ICourse) => void;
}

export const CourseCard: React.FC<CourseCardProps> = ({
  course, onSelect, onMoveToAddQuestions, onDeleteCourse, onEditDetails, 
  onHideCourse, onDeleteQuestions,
}) => {
  const [processing, setProcessing] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [shouldHide, setShouldHide] = useState<BooleanLiteral>("false");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteQuestionsModalOpen, setDeleteQuestionsModalOpen] =
    useState(false);
  const { openModal, closeModal } = useModalStore();

  const hasMoreYears = course?.availableQuizzes.length > 4;

  const isPremium = course?.courseType === "premium";

  return (
    <>
      <CardContainer onClick={e => onSelect?.(course)}>
        <CardImage imageUrl={course?.image}>
          <MoreOptionsButton onClick={event => {
            event.stopPropagation();
            setMenuOpen(true);
          }}>
            <span>...</span>
          </MoreOptionsButton>
          {course?.isHidden === "true" && <HiddenBadge>Hidden</HiddenBadge>}
          <CardBadge isPremium={isPremium}>
            {isPremium ? "Premium" : "Free"}
          </CardBadge>
          <CourseMenu
            {...{
              isHidden: Boolean(course) && course?.isHidden === "true",
              isOpen: menuOpen,
              onClose() {
                setMenuOpen(false);
              },
              onAddQuestions() {
                onMoveToAddQuestions(course) 
              }, 
              onEditDetails: () => onEditDetails(course), 
              onDeleteCourse() {
                setMenuOpen(false);
                openModal(
                  "Are you sure you want to delete this course?",
                  "Once deleted, it will be permanently removed and cannot be recovered. This action is irreversible.",
                  async () => {
                    if(!course) return;
                    await onDeleteCourse(course.id);
                    closeModal();
                  },
                  true
                )
              },
              onHideCourse(shouldHide) {
                setMenuOpen(false);
                setHideModalOpen(true);
                setShouldHide(shouldHide);
              },
            }}
          />
        </CardImage>
        <CardContent>
          <CardTitle>{course?.title}</CardTitle>
          <QuestionsAvailableText>
            Available past questions:
          </QuestionsAvailableText>
          {course?.availableQuizzes.length ? (
            <YearsContainer>
              {course?.availableQuizzes?.slice(0, 4).map((quiz) => (
                <YearBadge
                  key={quiz.quizId}
                  onClick={(e) => {
                    e.stopPropagation();
                    // setDeleteQuestionsModalOpen(true);
                  }}
                >
                  {quiz.quizYear}
                </YearBadge>
              ))}
              {hasMoreYears && (
                <MoreYearsBadge>+{course.availableQuizzes.length - 4}</MoreYearsBadge>
              )}
            </YearsContainer>
          ) : (
            <div style={{ marginTop: "4px", fontSize: "14px", color: "#999" }}>
              Not available
            </div>
          )}
        </CardContent>
      </CardContainer>

      <HideCourseModal
        isOpen={hideModalOpen}
        onClose={() => setHideModalOpen(false)}
        onConfirm={() => {
          setProcessing(true);
          onHideCourse(course.id, shouldHide).finally(() => {
            setProcessing(false)
            setHideModalOpen(false)
            setShouldHide("false")
          })
        }}
      />

      <DeleteCourseModal
        isOpen={deleteModalOpen}        
        onClose={() => setDeleteModalOpen(false)}
        processing={processing}
        onConfirm={() => {
          setProcessing(true)
          onDeleteCourse(course.id)
          .then(() => setDeleteModalOpen(false))
          .finally(() => setProcessing(false))
        }}
      />

      <DeleteCourseModal
        isOpen={deleteQuestionsModalOpen}
        onClose={() => setDeleteQuestionsModalOpen(false)}
        processing={processing}
        onConfirm={() => {
          setProcessing(true);
          onDeleteQuestions(course.id)
          .then(() => setDeleteQuestionsModalOpen(false))
          .finally(() => setProcessing(false))
        }}
        isQuestion={true}
      />
    </>
  );
};
